import { useGLTF } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import * as THREE from 'three'
import { getRacerIdentity } from '../game/racer-config'
import type { GameRuntime } from '../game/runtime'
import { RacerNameplate } from './RacerNameplate'
import {
  CAR_RIDE_HEIGHT,
  dampVisualAngle,
  getWheelSpinDelta,
  isVisualTeleport,
  PREMIUM_CAR_MODEL_URL,
  PREMIUM_CAR_MODEL_YAW,
  WHEEL_RIG,
} from './scene-contract'
import { WindowGunner } from './WindowGunner'

interface CarProps {
  runtime: GameRuntime
  vehicleId: string
  index: number
}

function asMaterials(material: THREE.Material | THREE.Material[]): THREE.Material[] {
  return Array.isArray(material) ? material : [material]
}

function tuneMaterial(material: THREE.Material): THREE.Material {
  const tuned = material.clone()
  if (!(tuned instanceof THREE.MeshStandardMaterial)) return tuned
  if (tuned.name === 'FrameBlack') {
    tuned.metalness = 0.75
    tuned.roughness = 0
    tuned.color.set('black')
  } else if (tuned.name === 'Chrome') {
    tuned.metalness = 1
    tuned.roughness = 0
    tuned.color.set('#333333')
  } else if (tuned.name === 'BreakDiscs') {
    tuned.metalness = 0.2
    tuned.roughness = 0.2
    tuned.color.set('#555555')
  } else if (tuned.name === 'TiresGum') {
    tuned.metalness = 0
    tuned.roughness = 0.4
    tuned.color.set('#181818')
  } else if (tuned.name === 'GreyElements') {
    tuned.metalness = 0
    tuned.color.set('#292929')
  } else if (tuned.name === 'emitbrake' || tuned.name === 'LightsFrontLed') {
    tuned.emissiveIntensity = 4
    tuned.toneMapped = false
  }
  tuned.needsUpdate = true
  return tuned
}

interface PreparedCar {
  root: THREE.Group
  wheels: Array<{ wheel: THREE.Object3D; direction: number }>
}

function prepareCarScene(source: THREE.Group): PreparedCar {
  const scene = source.clone(true)
  scene.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return
    if (object.name.startsWith('glass')) object.geometry.computeVertexNormals()
    if (object.name === 'glass_003') object.scale.setScalar(2.7)
    if (object.name === 'yellow_WhiteCar_0') {
      object.material = new THREE.MeshPhysicalMaterial({
        name: 'TrideBlackPaint',
        color: '#08090c',
        roughness: 0.22,
        metalness: 0.08,
        envMapIntensity: 1.15,
        clearcoat: 1,
        clearcoatRoughness: 0.025,
      })
    } else {
      const tuned = asMaterials(object.material).map(tuneMaterial)
      object.material = Array.isArray(object.material) ? tuned : (tuned[0] ?? object.material)
    }
    object.castShadow = true
    object.receiveShadow = true
  })
  const wheels = WHEEL_RIG.flatMap(({ name, direction }) => {
    const wheel = scene.getObjectByName(name)
    return wheel ? [{ wheel, direction }] : []
  })
  return { root: scene, wheels }
}

function createNumberTexture(number: number, accent: string): THREE.CanvasTexture {
  const canvas = document.createElement('canvas')
  canvas.width = 128
  canvas.height = 128
  const context = canvas.getContext('2d')
  if (context) {
    context.clearRect(0, 0, 128, 128)
    context.fillStyle = 'rgba(2, 4, 8, 0.84)'
    context.fillRect(8, 8, 112, 112)
    context.strokeStyle = accent
    context.lineWidth = 6
    context.strokeRect(8, 8, 112, 112)
    context.fillStyle = '#f7fbff'
    context.font = 'bold 72px Arial Narrow, sans-serif'
    context.textAlign = 'center'
    context.textBaseline = 'middle'
    context.fillText(String(number).padStart(2, '0'), 64, 68)
  }
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = 4
  return texture
}

export function Car({ runtime, vehicleId, index }: CarProps) {
  const outerRef = useRef<THREE.Group>(null)
  const bodyRef = useRef<THREE.Group>(null)
  const { scene } = useGLTF(PREMIUM_CAR_MODEL_URL)
  const prepared = useMemo(() => prepareCarScene(scene), [scene])
  const initialVehicle = runtime.getState().vehicles.find((candidate) => candidate.id === vehicleId)
  const accent = getRacerIdentity(vehicleId).accent
  const numberTexture = useMemo(() => createNumberTexture(index + 1, accent), [accent, index])

  useFrame((_, delta) => {
    const outer = outerRef.current
    const body = bodyRef.current
    const vehicle = runtime.getState().vehicles.find((candidate) => candidate.id === vehicleId)
    if (!outer || !body || !vehicle) return
    if (isVisualTeleport({ x: outer.position.x, y: outer.position.z }, vehicle.position)) {
      outer.position.x = vehicle.position.x
      outer.position.z = vehicle.position.y
    } else {
      const transformDamping = 1 - Math.exp(-delta * 18)
      outer.position.x = THREE.MathUtils.lerp(outer.position.x, vehicle.position.x, transformDamping)
      outer.position.z = THREE.MathUtils.lerp(outer.position.z, vehicle.position.y, transformDamping)
    }
    outer.position.y = CAR_RIDE_HEIGHT
    outer.rotation.y = dampVisualAngle(outer.rotation.y, vehicle.heading, delta)
    const motion = 1 - Math.exp(-delta * 9)
    body.rotation.z = THREE.MathUtils.lerp(body.rotation.z, -vehicle.steer * 0.035, motion)
    body.rotation.x = THREE.MathUtils.lerp(body.rotation.x, vehicle.drift.active ? 0.014 : 0, motion)
    for (const { wheel, direction } of prepared.wheels) {
      wheel.rotateX(getWheelSpinDelta(vehicle.speed, delta, direction))
    }
    outer.scale.setScalar(1)
  })

  return (
    <group
      ref={outerRef}
      position={[initialVehicle?.position.x ?? 0, CAR_RIDE_HEIGHT, initialVehicle?.position.y ?? 0]}
      rotation={[0, initialVehicle?.heading ?? 0, 0]}
      userData={{ testId: 'premium-car', model: PREMIUM_CAR_MODEL_URL, vehicleId }}
    >
      <group ref={bodyRef}>
        <primitive object={prepared.root} scale={0.015} rotation={[0, PREMIUM_CAR_MODEL_YAW, 0]} />
        <WindowGunner runtime={runtime} vehicleId={vehicleId} accent={accent} />
        <mesh position={[0, 1.62, -0.15]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.78, 0.78]} />
          <meshBasicMaterial map={numberTexture} transparent toneMapped={false} depthWrite={false} />
        </mesh>
      </group>
      <RacerNameplate runtime={runtime} vehicleId={vehicleId} />
      <mesh position={[0, -0.56, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[2.2, 32]} />
        <meshBasicMaterial
          color={accent}
          transparent
          opacity={0.12}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
      <pointLight color={accent} intensity={3.4} distance={7} decay={2} position={[0, -0.2, 0]} />
    </group>
  )
}

useGLTF.preload(PREMIUM_CAR_MODEL_URL)
